import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import type { CustomButtonEventPayload } from '@components/sp-custom-button';
import type { ShadowSettings } from '@common/types/shadow-settings.type';
import type { WebLegacyWidgetEntity } from '@web/types';
import type { OnInit } from '@angular/core';

import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';

import { EButtonStyleType, EButtonType } from '@common/enums';
import type { EIconType, ESocialNetworks } from '@common/enums';
import { ERadioGroupType } from '@ui/sp-radio-group';

import type { ExtendedPositionLegacy, SocialEntity, SocialItem, WidgetPositionLegacy } from '@sites/entity-core';
import { createSocialItem } from '@sites/entity-core';
import type { SocialDefaultForm, SocialForm, SocialZeroForm } from '@sites/entity-signal-form';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { ICON_NAMES, ICON_TYPES } from '@web/widgets/social/constants/social-widget.const';
import { getSocialNetworkIconSrc } from '@web/shared/components/web-social-networks/utils';
import { WebCustomButtonComponent } from '@web/shared/components/web-custom-button/web-custom-button.component';
import { WebSocialNetworksComponent } from '@web/shared/components/web-social-networks/web-social-networks.component';
import { WebWidgetPositionSettingsComponent } from '@web/shared/components/web-widget-position-settings';
import { WebWidgetHideSettingsComponent } from '@web/shared/components/web-widget-hide-settings';
import { WebWidgetExtendedHideSettingsComponent } from '@web/shared/components/web-widget-extended-hide-settings';
import { SpExtendedPositionSignalSettingsComponent } from '@components/sp-extended-position-signal-settings';
import { SpShadowSettingsSignalLegacyComponent } from '@components/sp-shadow-settings-signal-legacy';

import { SocialSettingsComponent } from './social-settings/social-settings.component';

@Component({
    selector: 'web-social-control',
    templateUrl: './social-control.component.html',
    styleUrls: ['./social-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        DragDropModule,
        SocialSettingsComponent,
        WebCustomButtonComponent,
        WebSocialNetworksComponent,
        WebWidgetPositionSettingsComponent,
        WebWidgetHideSettingsComponent,
        WebWidgetExtendedHideSettingsComponent,
        SpExtendedPositionSignalSettingsComponent,
        SpShadowSettingsSignalLegacyComponent,
    ],
})
export class SocialControlComponent implements OnInit {
    public readonly widget = input.required<WebLegacyWidgetEntity<SocialEntity['data']>>();
    public readonly isSpecific = input<boolean>(false);
    public readonly isEduSite = input<boolean>(false);

    private readonly translateService: TranslateService = inject(TranslateService);
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    public readonly ICON_SIZE_MAX: number = 200;

    public readonly EButtonType = EButtonType;
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly EButtonStyleType = EButtonStyleType;

    public readonly ICON_TYPES = ICON_TYPES;
    public readonly ICON_NAMES = ICON_NAMES;

    public readonly EMPTY_LINK_TEXT = this.translateService.instant('provide_link');

    public readonly isDeviceDesktopSig = toSignal(this.layoutFacade.isDeviceDesktop$, { initialValue: true });
    public readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });
    public readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });

    public form!: SocialForm;

    public readonly editableSocialIndex = signal<number | null>(null);

    public ngOnInit(): void {
        this.destroyRef.onDestroy(() => {
            if (this.form?.inTransaction()) {
                this.form.rollbackTransaction();
            }
        });

        this.form = this.signalFormService.getOrCreateScoped<SocialForm>(this.widget().id, this.destroyRef);
    }

    /** True when the widget lives inside a zero block — drives template branching. */
    public get isZero(): boolean {
        return this.widget().type === 'zero';
    }

    /** The form narrowed to the default variant — see the button control for the casting rationale. */
    public get defaultForm(): SocialDefaultForm {
        return this.form as SocialDefaultForm;
    }

    /** The form narrowed to the zero variant. */
    public get zeroForm(): SocialZeroForm {
        return this.form as SocialZeroForm;
    }

    public get isIcon(): boolean {
        return this.form?.appearanceType().value() === EButtonType.icon;
    }

    // --- Socials list ---

    private getUnselectedSocialNetwork(): ESocialNetworks {
        const selected = this.form.socialItems().map((item) => item.socialNetwork);
        const difference = this.ICON_NAMES.filter(
            (network) => !selected.includes(network as SocialItem['socialNetwork'])
        );

        return difference.length > 0 ? difference[0] : ICON_NAMES[0];
    }

    public dropElementItem(event: CdkDragDrop<SocialItem[]>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        // `moveItemInArray` mutates, so reorder a copy and write it back whole.
        const socials = [...this.form.socialItems()];
        moveItemInArray(socials, event.previousIndex, event.currentIndex);
        this.form.setSocials(socials);
    }

    public addSocial(): void {
        const network = this.getUnselectedSocialNetwork();

        this.form.addSocial(
            createSocialItem({
                socialNetwork: network as SocialItem['socialNetwork'],
                type: this.form.appearanceType().value(),
                iconType: this.form.iconType().value(),
                name: network,
            })
        );
    }

    public editSocial(index: number): void {
        // The settings panel edits the live form; the transaction makes
        // cancel/escape restore the pre-edit state.
        this.form.beginTransaction();
        this.editableSocialIndex.set(index);
    }

    public saveSocialSettings(): void {
        this.form.commitTransaction();
        this.editableSocialIndex.set(null);
    }

    public cancelSocialSettings(): void {
        this.form.rollbackTransaction();
        this.editableSocialIndex.set(null);
    }

    public removeSocial(index: number): void {
        if (this.form.socialItems().length === 1) {
            return;
        }

        this.form.removeSocial(index);
    }

    public getSocialIconSrcBySocial(social: SocialItem): string {
        return getSocialNetworkIconSrc(social.iconType, social.socialNetwork);
    }

    public getSocialSubText(social: SocialItem): string {
        return social.link.length ? social.link : this.EMPTY_LINK_TEXT;
    }

    // --- Shared appearance ---

    /** Appearance toggles update the shared field AND mirror it onto every item. */
    public onSwitchButtonType(type: EButtonType): void {
        const appearance = type as SocialItem['type'];

        this.form.setSocials(this.form.socialItems().map((social) => ({ ...social, type: appearance })));
        this.form.setAppearanceType(appearance);
    }

    public setIconType(iconType: EIconType): void {
        const icon = iconType as SocialItem['iconType'];

        this.form.setSocials(this.form.socialItems().map((social) => ({ ...social, iconType: icon })));
        this.form.setIconType(icon);
    }

    public setStyleType(event: Event): void {
        const value = (event.target as HTMLSelectElement).value as SocialEntity['data']['styleType'];
        this.form.setStyleType(value);
    }

    public setButtonSize(value: string): void {
        this.form.setButtonSize(value as SocialEntity['data']['buttonSize']);
    }

    public setHeight(value: number): void {
        this.form.setHeight(value);
    }

    public customButtonChangeHandler(changes: CustomButtonEventPayload): void {
        if (changes?.isIndependentCorners !== undefined) {
            this.form.setIsIndependentCorners(changes.isIndependentCorners);
        }

        if (changes?.radiusSettings) {
            this.form.setAdvancedRadius(changes.radiusSettings as SocialEntity['data']['advancedRadius']);
        }

        if (changes?.advancedBorderSettings) {
            this.form.setAdvancedBorderSettings(
                changes.advancedBorderSettings as unknown as SocialEntity['data']['advancedBorderSettings']
            );
        }

        this.form.setButtonBase(changes.base as unknown as SocialEntity['data']['buttonBase']);
        this.form.setButtonHover(changes.hover as unknown as SocialEntity['data']['buttonHover']);
    }

    // --- Shadow ---

    public setShadow(value: boolean): void {
        this.form.setIsShadow(value);
    }

    public setShadowSettings(settings: ShadowSettings): void {
        this.form.setShadowSettings(settings);
    }

    // --- Group layout ---

    public setHorizontalAlign(value: string): void {
        const align = value as SocialEntity['data']['horizontalAlign'];

        this.form.transact(() => {
            if (this.form.isSynchronized()) {
                this.form.setMobileHorizontalAlign(align);
                this.form.setTabletHorizontalAlign(align);
            }
            this.form.setHorizontalAlign(align);
        });
    }

    public setSynchronizeStyle(): void {
        const horizontalAlign = this.form.horizontalAlign().value();

        this.form.transact(() => {
            this.form.setMobileHorizontalAlign(horizontalAlign);
            this.form.setTabletHorizontalAlign(horizontalAlign);
            this.form.setIsSynchronizeStyle(!this.form.isSynchronized());
        });
    }

    // --- Layout (default variant) ---

    /**
     * Snapshot for the value/event-based `web-widget-position-settings`
     * component. `computed()` so the binding re-evaluates when the form's
     * position branch changes.
     */
    public readonly defaultPosition = computed<WidgetPositionLegacy | null>(() => {
        const f = this.form as SocialDefaultForm | undefined;
        if (!f || this.isZero) return null;
        return (f.position().value() as WidgetPositionLegacy) ?? null;
    });

    public onDefaultPositionChange(patch: Partial<WidgetPositionLegacy>): void {
        const current = this.defaultForm.position().value();
        this.defaultForm.setPosition({ ...current, ...patch });
    }

    // --- Layout (zero variant) ---

    /** See the button control's `zeroPosition` — same contract. */
    public readonly zeroPosition = computed<ExtendedPositionLegacy | null>(() => {
        const f = this.form as SocialZeroForm | undefined;
        if (!f || !this.isZero) return null;
        return f.position().value() as ExtendedPositionLegacy;
    });

    // --- Misc ---

    public updateWidgetDisplayId(displayId: string): void {
        this.widgetsFacade.updateWidgetDisplayId(this.widget().id, displayId);
    }
}
